import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star } from "lucide-react";

const Stars = ({ value, onSelect, size = "h-5 w-5" }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type={onSelect ? "button" : undefined} onClick={onSelect ? () => onSelect(n) : undefined}
        className={onSelect ? "cursor-pointer" : "cursor-default"} data-testid={onSelect ? `star-${n}` : undefined}>
        <Star className={`${size} ${n <= value ? "fill-gold text-gold" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export const PropertyReviews = ({ propertyId }) => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/properties/${propertyId}/reviews`); setReviews(data || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Indiquez votre nom"); return; }
    setSending(true);
    try {
      await api.post(`/properties/${propertyId}/reviews`, form);
      toast.success("Merci pour votre avis !");
      setForm({ name: "", rating: 5, comment: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  return (
    <div className="mt-8" data-testid="reviews-section">
      <div className="flex items-center gap-3">
        <h2 className="font-serif text-2xl font-semibold text-navy">Avis des voyageurs</h2>
        {avg && <span className="flex items-center gap-1.5 text-navy font-semibold"><Star className="h-5 w-5 fill-gold text-gold" /> {avg} · {reviews.length} avis</span>}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {reviews.length === 0 && <p className="text-sm text-slate-500">Aucun avis pour le moment. Soyez le premier !</p>}
        {reviews.map((r) => (
          <div key={r.id} className="rounded-xl bg-white border border-black/5 p-4" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-navy">{r.name}</span>
              <Stars value={r.rating} size="h-4 w-4" />
            </div>
            {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
            <p className="mt-2 text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 rounded-xl bg-ivory border border-black/5 p-5 space-y-3" data-testid="review-form">
        <h3 className="font-semibold text-navy">Laisser un avis</h3>
        <Stars value={form.rating} onSelect={(n) => setForm({ ...form, rating: n })} />
        <Input required placeholder="Votre nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" />
        <Textarea rows={3} placeholder="Partagez votre expérience..." value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment" />
        <Button type="submit" disabled={sending} className="bg-navy text-white rounded-lg" data-testid="review-submit">{sending ? "Envoi..." : "Publier mon avis"}</Button>
      </form>
    </div>
  );
};
